import { RenderTexture, type GlProgram, type TextureSource } from 'pixi.js';
import type { FilterContext, FilterProbeResult, ShaderPass, VideoFilterDefinition, VideoFilterFrame, VideoFilterRuntime } from '../core/definitions';
import type { FilterInstance } from '../core/model';
import type { Registry } from '../core/registry';
import type { AssetResolver, Logger } from '../core/services';
import { PassShader, type PassRunner, type ShaderPrograms, type TextureResolver } from './programs';
import { FILTER_BUILTINS, wrapFilterFragment } from './shaderWrap';

/** Largest render-target edge in physical pixels. */
export const MAX_TARGET_EDGE = 8192;

/** Shared GPU services plus the state of the render pass in progress. */
export interface RenderEnv {
  programs: ShaderPrograms;
  runner: PassRunner;
  registry: Registry;
  assets: AssetResolver;
  logger: Logger;
  resolveTexture: TextureResolver;
  /** Bumped whenever a definition is registered or removed. */
  registryVersion: number;
}

interface PassState {
  fragment: string;
  origin: boolean;
  shader: PassShader | null;
}

const ORIGIN_BUILTINS: ReadonlySet<string> = new Set([...FILTER_BUILTINS, 'uOrigin']);

interface Slot {
  instance: FilterInstance;
  definition: VideoFilterDefinition;
  runtime: VideoFilterRuntime | null;
  passes: PassState[];
  invalidated: boolean;
  errorLogged: boolean;
}

export function createTarget(width: number, height: number, resolution: number): RenderTexture {
  return RenderTexture.create({ width, height, resolution, antialias: false });
}

/** The PassShader the chain draws a pass with (also what probeFilterPass warms). */
function createPassShader(program: GlProgram, fragment: string, origin: boolean): PassShader {
  return new PassShader(program, fragment, origin ? ORIGIN_BUILTINS : FILTER_BUILTINS, origin ? ['uSource', 'uOrigin'] : ['uSource'], {
    uSourceSize: { value: new Float32Array(2), type: 'vec2<f32>' },
    uTime: { value: 0, type: 'f32' },
  });
}

export interface ProbeGpu {
  /** True while the GPU cannot be used (not initialised, destroyed or context lost); re-read after the draw. */
  lost(): boolean;
  programs: ShaderPrograms | null;
  runner: PassRunner | null;
}

/**
 * Compiles a filter pass through the shared program cache, then draws it once
 * with the same PassShader layout the chain uses into a throwaway 1x1 target,
 * so PixiJS links and caches its own program now instead of on the first
 * on-air frame. Never draws the program output.
 */
export function probeFilterPass(gpu: ProbeGpu, pass: Pick<ShaderPass, 'fragment' | 'originSampler'>, key: string): FilterProbeResult {
  if (gpu.lost() || !gpu.programs || !gpu.runner) return 'unavailable';
  const program = gpu.programs.get(wrapFilterFragment(pass.fragment), key);
  if (!program) return gpu.lost() ? 'unavailable' : 'failed';
  let shader: PassShader | null = null;
  let target: RenderTexture | null = null;
  try {
    shader = createPassShader(program, pass.fragment, pass.originSampler === true);
    target = createTarget(1, 1, 1);
    gpu.runner.draw(shader, target, 1, 1);
  } catch {
    return gpu.lost() ? 'unavailable' : 'failed';
  } finally {
    shader?.destroy();
    target?.destroy(true);
  }
  return gpu.lost() ? 'unavailable' : 'ok';
}

/** Resolution that keeps a target of the given logical size within MAX_TARGET_EDGE. */
export function clampResolution(width: number, height: number, resolution: number): number {
  const edge = Math.max(width, height) * resolution;
  return edge > MAX_TARGET_EDGE ? (resolution * MAX_TARGET_EDGE) / edge : resolution;
}

/**
 * The enabled video filters of one source or scene: owns their runtimes,
 * compiled pass shaders and ping-pong render targets (a third one only while a
 * pass samples `uOrigin` that is itself one of the targets).
 */
export class FilterChain {
  private filters: readonly FilterInstance[] | null = null;
  private registryVersion = -1;
  private slots: Slot[] = [];
  private readonly targets: (RenderTexture | null)[] = [null, null, null];
  private readonly frame: VideoFilterFrame = { width: 0, height: 0, timeMs: 0 };
  private programCallbacks: Array<{ callback: (frame: VideoFilterFrame) => void; frame: VideoFilterFrame }> = [];

  constructor(private readonly env: RenderEnv) {}

  /** Reconciles runtimes with the document; a no-op while the filter list identity is unchanged. */
  sync(filters: readonly FilterInstance[] | undefined): void {
    const list = filters ?? null;
    if (list === this.filters && this.registryVersion === this.env.registryVersion) return;
    this.filters = list;
    this.registryVersion = this.env.registryVersion;

    const previous = this.slots;
    const next: Slot[] = [];
    for (const instance of list ?? []) {
      if (!instance.enabled) continue;
      const definition = this.env.registry.getVideoFilter(instance.kind);
      if (!definition) continue;
      const index = previous.findIndex((slot) => slot.instance.id === instance.id && slot.definition === definition);
      if (index >= 0) {
        const slot = previous.splice(index, 1)[0]!;
        if (slot.instance.settings !== instance.settings && slot.runtime) {
          try {
            slot.runtime.update(instance.settings);
          } catch (error) {
            this.env.logger.log('error', 'render', `Filter "${instance.kind}" failed to update`, error);
          }
        }
        slot.instance = instance;
        next.push(slot);
      } else {
        next.push(this.createSlot(instance, definition));
      }
    }
    for (const slot of previous) this.destroySlot(slot);
    this.slots = next;
  }

  /** Runs every pass and returns the final picture; returns `input` when nothing ran. */
  run(input: TextureSource, width: number, height: number, resolution: number, timeMs: number): TextureSource {
    this.programCallbacks = [];
    if (this.slots.length === 0) return input;
    const frame = this.frame;
    frame.width = width;
    frame.height = height;
    frame.timeMs = timeMs;
    let current = input;
    for (let s = 0; s < this.slots.length; s++) {
      const slot = this.slots[s]!;
      if (!slot.runtime) continue;
      let passes;
      try {
        passes = slot.runtime.passes(frame);
      } catch (error) {
        this.logOnce(slot, 'failed while building its passes', error);
        continue;
      }
      if (passes.length === 0 || !this.prepare(slot, passes)) continue;
      // A pass whose required texture is missing skips the whole filter this
      // frame: running only some of its passes would be a partial picture.
      if (passes.some((pass) => pass.requireTextures && this.missingTexture(pass))) continue;
      const origin = current;
      const staged = this.programCallbacks.length;
      for (let i = 0; i < passes.length; i++) {
        const pass = passes[i]!;
        const shader = slot.passes[i]!.shader!;
        const free = this.freeTarget(current, pass.originSampler ? origin : null);
        if (free < 0) {
          // Never draw into a sampled texture: drop this filter's frame, keep the rest of the chain.
          this.logOnce(slot, 'found no render target free of its sampled textures', null);
          this.programCallbacks.length = staged;
          current = origin;
          break;
        }
        const target = this.target(free, width, height, resolution);
        shader.setSampler('uSource', current);
        if (pass.originSampler) shader.setSampler('uOrigin', origin);
        shader.setVec2('uSourceSize', width, height);
        shader.setFloat('uTime', timeMs / 1000);
        shader.applyUser(passes[i]!.uniforms, this.env.resolveTexture);
        this.env.runner.draw(shader, target, width, height);
        if (pass.onProgramApplied) this.programCallbacks.push({ callback: pass.onProgramApplied, frame: { ...frame } });
        current = target.source;
      }
    }
    return current;
  }

  /** The compositor calls this only after the current root scene result was successfully drawn to program. */
  notifyProgramApplied(): void {
    const callbacks = this.programCallbacks;
    this.programCallbacks = [];
    for (const { callback, frame } of callbacks) {
      try { callback(frame); } catch { /* A plugin observer cannot break the compositor. */ }
    }
  }

  /** Frees the render targets while keeping runtimes (the owner is idle but still in the document). */
  releaseTargets(): void {
    this.programCallbacks = [];
    for (let i = 0; i < this.targets.length; i++) {
      this.targets[i]?.destroy(true);
      this.targets[i] = null;
    }
  }

  destroy(): void {
    this.programCallbacks = [];
    for (const slot of this.slots) this.destroySlot(slot);
    this.slots = [];
    this.filters = null;
    this.releaseTargets();
  }

  /**
   * Compiles what changed; false when any pass of the filter is unusable (the
   * whole filter is skipped). Shaders past the passes asked for this frame are
   * kept, so a filter that trims trailing passes does not rebuild them.
   */
  private prepare(slot: Slot, passes: readonly ShaderPass[]): boolean {
    let usable = true;
    for (let i = 0; i < passes.length; i++) {
      const fragment = passes[i]!.fragment;
      const origin = passes[i]!.originSampler === true;
      let state = slot.passes[i];
      if (!state || state.fragment !== fragment || state.origin !== origin || slot.invalidated) {
        state?.shader?.destroy();
        const program = this.env.programs.get(wrapFilterFragment(fragment), slot.definition.kind);
        state = { fragment, origin, shader: program ? createPassShader(program, fragment, origin) : null };
        slot.passes[i] = state;
      }
      if (!state.shader) usable = false;
    }
    if (slot.invalidated) {
      while (slot.passes.length > passes.length) slot.passes.pop()?.shader?.destroy();
      slot.invalidated = false;
    }
    return usable;
  }

  private missingTexture(pass: ShaderPass): boolean {
    const uniforms = pass.uniforms;
    for (const name in uniforms) {
      const value = uniforms[name];
      if (typeof value === 'object' && value !== null && 'texture' in value && value.texture !== null && !this.env.resolveTexture(value.texture, value.version ?? 0)) return true;
    }
    return false;
  }

  /** First target that is neither read by the next pass nor its origin; -1 when none is (never draw into a sampled texture). */
  private freeTarget(source: TextureSource, origin: TextureSource | null): number {
    for (let i = 0; i < this.targets.length; i++) {
      const held = this.targets[i]?.source;
      if (!held || (held !== source && held !== origin)) return i;
    }
    // Unreachable with three targets and at most two sampled.
    return -1;
  }

  private target(index: number, width: number, height: number, resolution: number): RenderTexture {
    const w = Math.max(1, Math.ceil(width));
    const h = Math.max(1, Math.ceil(height));
    const res = clampResolution(w, h, resolution);
    let target = this.targets[index];
    if (!target) {
      target = createTarget(w, h, res);
      this.targets[index] = target;
    } else if (target.source.width !== w || target.source.height !== h || target.source.resolution !== res) {
      target.resize(w, h, res);
    }
    return target;
  }

  private createSlot(instance: FilterInstance, definition: VideoFilterDefinition): Slot {
    const slot: Slot = { instance, definition, runtime: null, passes: [], invalidated: false, errorLogged: false };
    const context: FilterContext = {
      assets: this.env.assets,
      logger: this.env.logger,
      invalidate: () => {
        slot.invalidated = true;
      },
    };
    try {
      slot.runtime = definition.create(context, instance.settings);
    } catch (error) {
      this.logOnce(slot, 'could not be created', error);
    }
    return slot;
  }

  private destroySlot(slot: Slot): void {
    for (const pass of slot.passes) pass.shader?.destroy();
    slot.passes = [];
    try {
      slot.runtime?.destroy();
    } catch (error) {
      this.env.logger.log('warn', 'render', `Filter "${slot.instance.kind}" threw while being destroyed`, error);
    }
    slot.runtime = null;
  }

  private logOnce(slot: Slot, what: string, error: unknown): void {
    if (slot.errorLogged) return;
    slot.errorLogged = true;
    this.env.logger.log('error', 'render', `Filter "${slot.instance.kind}" ${what} and is skipped`, error);
  }
}
